import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Search, Heart, ShoppingBag, Menu, X, Globe } from "lucide-react";
import { useApp } from "../context/AppContext";
import CartDrawer from "./CartDrawer";

const LANGS = [
  { code: "en", label: "EN" },
  { code: "de", label: "DE" },
];
const CURRENCIES = ["EUR", "USD", "GBP", "CHF"];

const Header = () => {
  const { t, language, setLanguage, currency, setCurrency, cartCount, wishlist, setCartOpen } = useApp();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  const navLinks = [
    { to: "/shop", label: t.nav.shop },
    { to: "/best-sellers", label: t.nav.bestSellers },
    { to: "/about", label: t.nav.about },
    { to: "/jewelry-info", label: t.nav.jewelryInfo },
    { to: "/contact", label: t.nav.contact },
  ];

  return (
    <>
      {/* Announcement bar */}
      <div className="bg-[var(--noir)] text-[var(--ivory)] text-[11px] tracking-[0.2em] uppercase py-2.5 overflow-hidden" data-testid="announcement-bar">
        <div className="flex animate-marquee whitespace-nowrap">
          {[...t.announcement, ...t.announcement, ...t.announcement, ...t.announcement].map((a, i) => (
            <span key={i} className="mx-8 flex items-center gap-8">
              <span className="text-[var(--gold)]">✦</span>{a}
            </span>
          ))}
        </div>
      </div>

      <header
        className={`sticky top-0 z-50 transition-all duration-500 ${
          scrolled ? "bg-[var(--ivory)]/95 backdrop-blur-xl border-b border-[var(--champagne)]/40" : "bg-[var(--ivory)]"
        }`}
        data-testid="main-header"
      >
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          {/* Mobile menu trigger */}
          <button
            className="lg:hidden p-2 -ml-2"
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-btn"
            aria-label="Menu"
          >
            <Menu className="w-5 h-5" />
          </button>

          {/* Left nav (desktop) */}
          <nav className="hidden lg:flex items-center gap-9">
            {navLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="link-lux text-[12px] tracking-[0.18em] uppercase font-medium text-[var(--noir)]"
                data-testid={`nav-${l.to.replace("/", "")}`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          {/* Logo center */}
          <Link to="/" className="absolute left-1/2 -translate-x-1/2" data-testid="logo">
            <div className="text-center leading-none">
              <div className="font-display text-2xl lg:text-3xl tracking-[0.18em] text-[var(--noir)]">LUXERA</div>
              <div className="text-[9px] tracking-[0.4em] text-[var(--gold-deep)] mt-1">KaLiMa</div>
            </div>
          </Link>

          {/* Right icons */}
          <div className="flex items-center gap-1 lg:gap-3">
            <div className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="hidden md:flex items-center gap-1.5 px-2 py-2 text-[11px] tracking-[0.18em] uppercase hover:text-[var(--gold-deep)] transition-colors"
                data-testid="lang-selector-btn"
              >
                <Globe className="w-4 h-4" />
                <span>{language.toUpperCase()}/{currency}</span>
              </button>
              {langOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setLangOpen(false)} />
                  <div className="absolute right-0 top-full mt-2 bg-white border border-[var(--champagne)] shadow-2xl p-5 z-50 min-w-[180px]" data-testid="lang-dropdown">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--grey)] mb-3">Language</div>
                    <div className="grid grid-cols-2 gap-1 mb-4">
                      {LANGS.map((l) => (
                        <button
                          key={l.code}
                          onClick={() => { setLanguage(l.code); setLangOpen(false); }}
                          className={`text-left px-2 py-1.5 text-[12px] tracking-[0.1em] uppercase ${language === l.code ? "bg-[var(--noir)] text-[var(--ivory)]" : "hover:bg-[var(--ivory-deep)]"}`}
                          data-testid={`lang-${l.code}`}
                        >
                          {l.label}
                        </button>
                      ))}
                    </div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--grey)] mb-3">Currency</div>
                    <div className="grid grid-cols-2 gap-1">
                      {CURRENCIES.map((c) => (
                        <button
                          key={c}
                          onClick={() => { setCurrency(c); }}
                          className={`text-left px-2 py-1.5 text-[12px] ${currency === c ? "bg-[var(--noir)] text-[var(--ivory)]" : "hover:bg-[var(--ivory-deep)]"}`}
                          data-testid={`curr-${c}`}
                        >
                          {c}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            <Link to="/shop" className="p-2 hover:text-[var(--gold-deep)] transition-colors" data-testid="search-btn" aria-label="Search">
              <Search className="w-5 h-5" />
            </Link>
            <Link to="/wishlist" className="p-2 hover:text-[var(--gold-deep)] transition-colors relative" data-testid="wishlist-btn" aria-label="Wishlist">
              <Heart className="w-5 h-5" />
              {wishlist.length > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-[var(--noir)] text-[var(--ivory)] text-[9px] w-4 h-4 flex items-center justify-center rounded-full">
                  {wishlist.length}
                </span>
              )}
            </Link>
            <button onClick={() => setCartOpen(true)} className="p-2 hover:text-[var(--gold-deep)] transition-colors relative" data-testid="cart-btn" aria-label="Cart">
              <ShoppingBag className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-[var(--gold)] text-[var(--noir)] text-[9px] w-4 h-4 flex items-center justify-center rounded-full font-medium" data-testid="cart-count">
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] bg-[var(--ivory)] lg:hidden" data-testid="mobile-menu">
          <div className="flex items-center justify-between p-6 border-b border-[var(--champagne)]/50">
            <div className="font-display text-2xl tracking-[0.18em]">LUXERA</div>
            <button onClick={() => setMobileOpen(false)} data-testid="close-mobile-menu"><X className="w-6 h-6" /></button>
          </div>
          <nav className="flex flex-col p-8 gap-6">
            {navLinks.map((l) => (
              <Link key={l.to} to={l.to} className="text-2xl font-display tracking-wide">
                {l.label}
              </Link>
            ))}
            <Link to="/wishlist" className="text-2xl font-display tracking-wide">{t.nav.wishlist}</Link>
            <Link to="/faq" className="text-2xl font-display tracking-wide">{t.nav.faq}</Link>
          </nav>
          <div className="p-8 border-t border-[var(--champagne)]/50 absolute bottom-0 left-0 right-0">
            <div className="grid grid-cols-2 gap-2 mb-4">
              {LANGS.map((l) => (
                <button key={l.code} onClick={() => setLanguage(l.code)} className={`py-2 text-xs tracking-[0.15em] ${language === l.code ? "bg-[var(--noir)] text-[var(--ivory)]" : "border border-[var(--champagne)]"}`}>
                  {l.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <CartDrawer />
    </>
  );
};

export default Header;
